import { useParsePaginationFromUrl } from '@app/hooks/useParsePaginationFromUrl';
import { useAuth0 } from '@auth0/auth0-react';
import SquareAd from '@components/Advertisements/SquareAd';
import { BoatCardImage } from '@components/BoatCardImage';
import { SingleCollectionSortByTogglers } from '@components/SingleCollectionSortByTogglers';
import { useAddToFavoriteBoat, useRemoveFavoriteBoat, useUserFavorites } from '@hooks';
import { PublicBoatCard } from '@marine/boats/src/components/PublicBoatCard';
import { event } from '@marine/lib';
import { getBoatName } from '@utils/getBoatName';
import { Col, Grid, Layout, Pagination, Row, Spin } from 'antd';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { FC } from 'react';
import { useSubscription } from 'services/hooks/queries/useSubscription';
import styles from './BoatsCardsList.module.scss';
import { BoatsCardsListProps } from './BoatsCardsList.props';

const { Content } = Layout;

const BoatsCardsList: FC<BoatsCardsListProps> = ({ boats, isSpinning }) => {
  const router = useRouter();
  const { isLoading: isLoadingAuthentication, isAuthenticated } = useAuth0();
  const { isSubscribed, isLoadingSubscription } = useSubscription();

  const { useBreakpoint } = Grid;
  const { md } = useBreakpoint();

  const { currentPage, currentPerPage } = useParsePaginationFromUrl();

  const onPaginationChange = (pageNr: number, perPageNr: number) => {
    router.query.page = `${pageNr}`;
    router.query.perPage = `${perPageNr}`;

    event({
      action: 'pagination_changed',
      category: 'user_action',
      label: 'Page number',
      value: perPageNr.toString(),
    });

    router.push(
      { pathname: router.pathname, query: router.query },
      { pathname: router.pathname, query: router.query },
      { shallow: true },
    );
  };

  const { reFetchFavorite, checkIsFavorite } = useUserFavorites();
  const { isLoadingRemove, removeFavoriteFn } = useRemoveFavoriteBoat();
  const { isLoadingAdd, addToFavoriteFn } = useAddToFavoriteBoat();

  return (
    <Content className={styles.boatsCardsListWrapper}>
      <Spin tip="Loading..." spinning={isSpinning}>
        <SingleCollectionSortByTogglers pagination={boats?.pageInfo} />
        <div className={styles.boatsCardsGrid}>
          <Row gutter={[8, 4]}>
            {md && !isSubscribed && !isLoadingAuthentication && !isLoadingSubscription && (
              <Col className={styles.itemCardCol} xxl={6} xl={6} lg={8} md={12}>
                <SquareAd />
              </Col>
            )}
            {boats?.nodes?.map((boat) => {
              const isFavourited = checkIsFavorite(Number(boat.documentId));

              return (
                <Col className={styles.itemCardCol} key={boat.documentId} xxl={6} xl={6} lg={8} md={12}>
                  <Link href={`/boat/${boat.slug}`}>
                    <PublicBoatCard
                      className={styles.itemCard}
                      boatModel={boat.model}
                      hasShadow
                      isFavorite={isFavourited}
                      // favorites_count={0}
                      views={boat?.views_count}
                      boatImage={<BoatCardImage image={boat.image} width={270} height={150} />}
                      showFavoriteToggle={isAuthenticated}
                      title={getBoatName(boat)}
                      disableFavoriteButton={isLoadingAdd || isLoadingRemove}
                      onFavoriteClick={() => {
                        if (!isFavourited) {
                          addToFavoriteFn(Number(boat.documentId), {
                            onSettled: () => {
                              reFetchFavorite();
                            },
                          });
                        } else {
                          removeFavoriteFn(Number(boat.documentId), {
                            onSettled: () => {
                              reFetchFavorite();
                            },
                          });
                        }
                      }}
                    />
                  </Link>
                </Col>
              );
            })}
          </Row>
        </div>
        <Row justify="end">
          <Col>
            {boats?.nodes?.length !== 0 && boats?.pageInfo?.pageCount > 1 && (
              <Pagination
                // disabled={isFetching}
                pageSizeOptions={['10', '15', '20', '50', '100']}
                // defaultCurrent={currentPage}
                current={currentPage}
                total={boats.pageInfo.total || 0}
                onChange={onPaginationChange}
                pageSize={currentPerPage}
              />
            )}
          </Col>
        </Row>
      </Spin>
    </Content>
  );
};

export { BoatsCardsList };
